import { cva } from 'class-variance-authority';
import { ElementRef, forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { WithIcon } from '../../../types/component.types';
import { Input } from '../Input';
import { InputFieldProps } from '../types';

const trailingIconClasses = cva(
  'absolute inset-y-s right-m flex items-center text-content-subdued pl-s',
  {
    variants: {
      pattern: {
        none: 'pointer-events-none bg-surface',
        clickable: 'cursor-pointer bg-transparent',
      },
    },
    defaultVariants: {
      pattern: 'none',
    },
  },
);

type InputElement = ElementRef<'input'>;
export type TextFieldProps = InputFieldProps &
  WithIcon & {
    ariaLabel?: string;
    prefix?: string;
    leadingMeta?: string;
    trailingMeta?: string;
  };

/**
 * TextField is a component that allows the user to input text.
 *
 * @param pattern - The functional mode of the text field.
 *        - "none" (default) - The text field tailing icon is not clickable.
 *        - "clickable" - The text field icon is clickable.
 */
// eslint-disable-next-line react-x/no-forward-ref -- TODO: Wait! Don't change this file without seeing if you can remove this disable. It was added during a bulk version update, but fixing it would leave the park nicer than you found it.
export const TextField = forwardRef<InputElement, TextFieldProps>(
  (
    {
      intent,
      size,
      leadingIcon,
      trailingIcon,
      leadingMeta,
      trailingMeta,
      ariaLabel,
      disabled,
      name,
      pattern = 'none',
      placeholder,
      onChange,
      type,
      ...restProps
    },
    forwardedRef,
  ) => {
    return (
      <div className="relative rounded-m">
        {(leadingMeta || leadingIcon) && (
          <div
            className={twMerge(
              'pointer-events-none absolute inset-y-s left-0 flex items-center pl-s text-content-subdued',
              intent == 'error' && 'text-negative',
            )}
          >
            {leadingMeta && (
              <span
                className={twMerge(
                  'text-content-subdued sm:text-sm',
                  intent === 'error' && 'text-negative',
                )}
              >
                {leadingMeta}
              </span>
            )}
            {leadingIcon}
          </div>
        )}
        <Input
          {...restProps}
          type={type ?? 'text'}
          size={size}
          intent={intent}
          name={name}
          aria-disabled={disabled}
          aria-label={ariaLabel}
          ref={forwardedRef}
          disabled={disabled}
          placeholder={placeholder}
          onChange={onChange}
          className={twMerge(
            leadingMeta && 'pl-xl',
            leadingIcon && 'pl-xxxl',
            (trailingMeta || trailingIcon) && 'pr-xxxl',
          )}
        />
        {(trailingMeta || trailingIcon) && (
          <div
            className={trailingIconClasses({
              pattern: pattern as 'none' | 'clickable',
            })}
          >
            {trailingMeta && (
              <span
                className={twMerge(
                  'text-content-subdued sm:text-sm',
                  intent === 'error' && 'text-negative',
                )}
              >
                {trailingMeta}
              </span>
            )}
            {trailingIcon}
          </div>
        )}
      </div>
    );
  },
);

TextField.displayName = 'TextField';
